import React, { useEffect, useState } from 'react';
import useFordRidesContext from '../hooks/use-ford-rides-context';
import { Pagination, FMDropdown } from '@own/fds-react';
import RidesFilter from '../rides-filter/rides-filter';
import filterIcon from '../../../../assets/ford-rides/Filter.svg';
import exportIcon from '../../../../assets/ford-rides/export.svg';
import businessIcon from '../../../../assets/ford-rides/business-type.svg';
import personalIcon from '../../../../assets/ford-rides/personal-type.svg';
import unassignedIcon from '../../../../assets/ford-rides/unassigned-type.svg';
import closeIcon from '../../../../assets/ford-rides/close.svg';
import { ReducerInitialState } from '../../../../models/ford-rides-trips-service-model';
import FordRidesAccordion from '../ford-rides-accordions/ford-rides-accordion/ford-rides-accordion';
import {
    ComponentType,
    RideType,
} from '../../../../views/ford-rides-view/ford-rides-view';
import { ReducerInitialStates } from '../../../../contexts/ford-rides-context/ford-rides-context';
import { PrimaryButton } from '../../../common';
import RidesExport from '../rides-export/rides-export';
import { FordRidesContentProps } from '../hooks/use-ford-rides-content';
import { useBreakpoints } from '../../../../hooks/use-breakpoints';
import './all-rides.scss';
import { useAnalytics } from '../../../../hooks/use-analytics';
import { UtilityButtons } from '../ford-rides-home/ford-rides-home';
import { isNumber } from '../../../../views/cx-dropdown/cx-dropdown';
import { Option } from '@own/fds-react/dist/types/fm-dropdown.types';
interface Props {
    rideType?: RideType[];
    setRideType?: (data: RideType[]) => void;
    setView?: (data: ComponentType) => void;
    fordRidesData: FordRidesContentProps;
}

export type displayUtilityType = 'Filter' | 'Export' | '';

const rideTypeImage = {
    business: businessIcon,
    personal: personalIcon,
    unassigned: unassignedIcon,
};

interface DropdownOptions extends Option {
    disabled: boolean;
}

function AllRides({ fordRidesData, ...props }: Props) {
    const [itemsPerPage, setItemsPerPage] = useState(
        parseInt(fordRidesData.paginationNumberList[0])
    );
    const { mobile } = useBreakpoints();
    const {
        FordRidesDateRangePicker,
        state,
        distanceDurationFilter,
    } = useFordRidesContext();
    const [tripId, setTripId] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [displayUtility, setDisplayUtility] = useState<displayUtilityType>(
        ''
    );
    const rideType = props.rideType;
    const rideData = rideType?.reduce(
        (prevValue: ReducerInitialState[], rideType) => {
            return [
                ...prevValue,
                ...(state[rideType] as ReducerInitialState[]),
            ];
        },
        []
    );
    const filterData =
        rideData?.map((accordionData: ReducerInitialState) => {
            return (
                <FordRidesAccordion
                    {...accordionData}
                    accordionCategory={
                        (accordionData.tripType ||
                            'unassigned') as keyof ReducerInitialStates
                    }
                    key={accordionData.tripRowId}
                    tripId={tripId}
                    setTripId={setTripId}
                    fordRidesData={fordRidesData}
                />
            );
        }) || [];
    const [fireEvents] = useAnalytics();
    const triggerLoadAnalytics = () => {
        fireEvents(
            ['target-trigger-view', 'ford-rides-all-rides'],
            'ford-rides-all-rides'
        );
    };
    useEffect(() => {
        if (!rideData?.length) {
            props?.setView && props?.setView(ComponentType.FordRidesHome);
        }
    }, [rideData?.length]);

    useEffect(() => {
        triggerLoadAnalytics();
        window.scrollTo({
            top: 0,
            behavior:
                'scrollBehavior' in document.documentElement.style
                    ? 'smooth'
                    : 'auto',
        });
    }, []);

    const handleRemoveFilter = (value: RideType) => {
        const isPresent = rideType?.includes(value);
        const notRemovable = (rideType || []).length < 2;
        const currentData =
            isPresent && !notRemovable
                ? rideType?.filter(i => i !== value)
                : [...(rideType || [])];
        props?.setRideType && props?.setRideType(currentData as RideType[]);
    };

    function handleUtility(event: React.MouseEvent<HTMLButtonElement>) {
        const utilityButton = event.currentTarget as HTMLButtonElement;
        const ariaLabel = (utilityButton.getAttribute('aria-label') ||
            utilityButton.ariaLabel) as displayUtilityType;
        setDisplayUtility(prevState => {
            if (prevState === ariaLabel) {
                return '';
            }
            return ariaLabel;
        });
    }

    function utilityButtons(displayUtility: displayUtilityType) {
        switch (displayUtility) {
            case 'Filter':
                return (
                    <RidesFilter
                        rideType={props.rideType}
                        state={state}
                        setRideType={props.setRideType}
                        fordRidesData={fordRidesData}
                    />
                );
            case 'Export':
                return <RidesExport rideType={props.rideType} />;
            default:
                return <></>;
        }
    }
    const handleItemsPerPageChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setItemsPerPage(parseInt(event.target.value));
    };
    const totalRides = rideData?.length || 0;
    const isDropdownDisabled =
        totalRides <
        Math.min(...fordRidesData.paginationNumberList.map(Number));

    function getFilterCount() {
        let count = (rideType || []).length;
        const distanceFilterKeyValue = {
            distanceFrom: distanceDurationFilter.distanceFrom,
            distanceTo: distanceDurationFilter.distanceTo,
        };
        const durationFilterKeyValue = {
            durationFrom: distanceDurationFilter.durationFrom,
            durationTo: distanceDurationFilter.durationTo,
        };
        if (
            distanceFilterKeyValue.distanceFrom ||
            distanceFilterKeyValue.distanceTo
        ) {
            count++;
        }
        if (
            durationFilterKeyValue.durationFrom ||
            durationFilterKeyValue.durationTo
        ) {
            count++;
        }
        return count;
    }

    const dropdownOptions: DropdownOptions[] = fordRidesData.paginationNumberList
        .filter(number => number && isNumber(number))
        .map(number => ({
            label: number.toString(),
            value: number.toString(),
            disabled: parseInt(number) > totalRides,
        }));
    return (
        <div className="all-rides-home">
            <h1 className="all-rides-title">{fordRidesData.allRidesHeading}</h1>
            <div className="filter-date-pannel">
                <span className="filtered-cards">
                    {rideType?.map(filterType => {
                        return (
                            <span
                                className="filter-card"
                                key={filterType}
                                aria-disabled={(rideType || []).length < 2}
                            >
                                <img
                                    src={rideTypeImage[filterType]}
                                    alt={filterType}
                                />
                                &nbsp;
                                {fordRidesData[filterType]}
                                &nbsp;
                                <button
                                    disabled={(rideType || []).length < 2}
                                    onClick={() =>
                                        handleRemoveFilter(filterType)
                                    }
                                >
                                    <img
                                        className="close-icon"
                                        src={closeIcon}
                                        alt="clear"
                                    />
                                </button>
                            </span>
                        );
                    })}
                </span>
                <span className="date-picker">{FordRidesDateRangePicker}</span>
            </div>
            <div className="more-options__wrapper">
                <div className="utility-navigation">
                    <PrimaryButton
                        color="dark"
                        fill="outline"
                        chevronDirection="left"
                        chevron={true}
                        onClick={() => {
                            fireEvents(
                                ['ford-rides-back-to-landing'],
                                undefined,
                                undefined,
                                false
                            );
                            props?.setRideType?.([]);
                            props?.setView?.(ComponentType.FordRidesHome);
                        }}
                    >
                        {fordRidesData.fordRidesCtaText}
                    </PrimaryButton>
                    <UtilityButtons
                        getFilterCount={getFilterCount}
                        handleUtility={handleUtility}
                        filterIcon={filterIcon}
                        exportIcon={exportIcon}
                        state={state}
                    />
                </div>
                {utilityButtons(displayUtility)}
            </div>
            <div className="rides-container">
                <h3>{rideData?.length + ' ' + fordRidesData.ridesText}</h3>
                <Pagination
                    dataItems={filterData}
                    isMobile={mobile}
                    itemsPerPage={itemsPerPage}
                    pageNumberTitle={fordRidesData.pageNumberTitle}
                    prevButtonLabel={fordRidesData.prevButtonLabel}
                    prevButtonTitle={fordRidesData.prevButtonTitle}
                    nextButtonLabel={fordRidesData.nextButtonLabel}
                    nextButtonTitle={fordRidesData.nextButtonTitle}
                    ofLabel={fordRidesData.ofLabel}
                    morePagesAriaLabel={fordRidesData.morePagesAriaLabel}
                    currentPage={currentPage}
                    updateCurrentPageNumber={data => setCurrentPage(data)}
                />
                <FMDropdown
                    ariaLabel={fordRidesData.paginationNumberChangeText}
                    value={itemsPerPage}
                    disabled={isDropdownDisabled}
                    options={dropdownOptions}
                    onChange={handleItemsPerPageChange}
                    header={fordRidesData.paginationNumberChangeText}
                    id="itemsPerPageSelect"
                    customClass="pagination-controls"
                    renderOwnOption={(options: string[] | Option[]) => {
                        const newOptions = options as DropdownOptions[];
                        return (
                            <>
                                {newOptions.map(option => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                        disabled={option.disabled}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </>
                        );
                    }}
                />
            </div>
        </div>
    );
}

export default AllRides;
